import { ReactElement, useMemo, useState } from "react";
import { FileDown } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { CsvField, CsvImporter } from "@/components/_organisms/csv-import-dialog/csv-importer";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useCreateMunicipalProperty } from "@/domain/municipal-property/hooks/api/use-create-municipal-property";
import { downloadPropertyImportTemplate } from "@/domain/municipal-property/lib/csv-import/download-property-import-template";
import {
  importPropertyCsv,
  PropertyCsvImportStatus,
} from "@/domain/municipal-property/lib/csv-import/import-property-csv";
import { validatePropertyCsvRow } from "@/domain/municipal-property/lib/csv-import/validate-property-csv-row";
import { refreshMunicipalPropertyManagement } from "@/domain/municipal-property/lib/refresh-municipal-property-management";
import { WorkspaceMunicipalityOption } from "@/domain/municipal-property/models/workspace-municipality-option.model";
import { MUNICIPAL_PROPERTY_TYPE_LIST } from "@ark/shared";
import { useQueryClient } from "@tanstack/react-query";

const MAX_PROPERTY_IMPORT_ROWS = 100;
const MAX_PROPERTY_IMPORT_BYTES = 2 * 1024 * 1024;
const PROPERTY_CSV_FIELD_LIST = [
  "name",
  "type",
  "effectiveYear",
  "municipality",
  "street",
  "houseNumber",
  "postalCode",
  "city",
  "usableFloorAreaM2",
  "commissioningYear",
  "description",
] as const;
interface MunicipalPropertyCsvImportProps {
  workspaceId: string;
  municipalityList: readonly WorkspaceMunicipalityOption[];
}

export function MunicipalPropertyCsvImport({
  workspaceId,
  municipalityList,
}: MunicipalPropertyCsvImportProps): ReactElement {
  const { t } = useDomainTranslation("municipal-property");
  const queryClient = useQueryClient();
  const createProperty = useCreateMunicipalProperty({ refresh: false });
  const [progress, setProgress] = useState<number | null>(null);
  const [result, setResult] = useState<{ name: string; status: PropertyCsvImportStatus }[] | null>(null);
  const [hasRefreshError, setHasRefreshError] = useState(false);
  const typeLabelMap = useMemo(
    () => Object.fromEntries(MUNICIPAL_PROPERTY_TYPE_LIST.map((type) => [type, t(`type.${type}`)])),
    [t],
  );
  const fields: CsvField[] = useMemo(
    () =>
      PROPERTY_CSV_FIELD_LIST.map((value) => ({
        value,
        label: t(
          value === "effectiveYear"
            ? "history.effectiveYear"
            : value === "municipality"
              ? "create.field.municipalityList"
              : `create.field.${value}`,
        ),
        required: value === "name" || value === "type",
        normalizeValue: (cellValue: string) => {
          const text = cellValue.trim();
          if (value === "type")
            return MUNICIPAL_PROPERTY_TYPE_LIST.find((type) => type === text || typeLabelMap[type] === text) ?? text;
          if (value !== "municipality") return cellValue;
          if (!text || text === "__unassigned__") return "__unassigned__";
          const matchList = municipalityList.filter(
            (municipality) => municipality.id === text || municipality.name === text,
          );
          return matchList.length === 1 ? matchList[0].id : text;
        },
        options:
          value === "type"
            ? MUNICIPAL_PROPERTY_TYPE_LIST.map((type) => ({ value: type, label: typeLabelMap[type] }))
            : value === "municipality"
              ? [
                  { value: "__unassigned__", label: t("municipalitySelect.workspaceWide") },
                  ...municipalityList.map((municipality) => ({
                    value: municipality.id,
                    label: municipality.name,
                  })),
                ]
              : undefined,
      })),
    [t, municipalityList, typeLabelMap],
  );
  const validateRow = (row: Record<string, unknown>) =>
    validatePropertyCsvRow({ row, workspaceId, municipalityList, typeLabelMap });
  function validateData(rowList: Record<string, unknown>[]): string | null {
    const seen = new Set<string>();
    for (const [index, row] of rowList.entries()) {
      const parsed = validateRow(row);
      if (!parsed.success)
        return t("csvImport.invalidRow", {
          row: index + 2,
          field: fields.find((field) => field.value === parsed.field)?.label ?? t("history.effectiveYear"),
        });
      const key = JSON.stringify(parsed.data);
      if (seen.has(key)) return t("csvImport.duplicateRow", { row: index + 2 });
      seen.add(key);
    }
    return null;
  }
  async function handleImport(
    rowList: Record<string, unknown>[],
    completed: (result: { success: boolean }) => void,
  ): Promise<void> {
    if (validateData(rowList)) {
      completed({ success: false });
      return;
    }
    const inputList = rowList.flatMap((row) => {
      const parsed = validateRow(row);
      return parsed.success ? [parsed.data] : [];
    });
    setProgress(0);
    try {
      const statusList = await importPropertyCsv({
        inputList,
        createProperty: createProperty.mutateAsync,
        onProgress: setProgress,
      });
      setResult(statusList.map((status, index) => ({ name: inputList[index].name, status })));
      const isSuccess = statusList.length > 0 && statusList.every((status) => status === "success");
      let didRefreshFail = false;
      await refreshMunicipalPropertyManagement({
        queryClient,
        scope: { workspaceId, propertyIdList: [] },
        throwOnError: true,
      }).catch(() => {
        didRefreshFail = true;
      });
      completed({ success: isSuccess });
      if (isSuccess) toast.success(t("csvImport.completed"));
      if (didRefreshFail) {
        if (isSuccess) toast.error(t("csvImport.savedRefreshError"));
        else setHasRefreshError(true);
      }
    } finally {
      setProgress(null);
    }
  }
  async function handleDownloadTemplate(): Promise<void> {
    await downloadPropertyImportTemplate({
      dataSheetName: t("csvImport.templateDataSheet"),
      helpSheetName: t("csvImport.templateHelpSheet"),
      headerList: fields.map((field) => field.label),
      helpRowList: [
        [t("csvImport.templateField"), t("csvImport.templateRequirement"), t("csvImport.templatePurpose")],
        ...PROPERTY_CSV_FIELD_LIST.map((value, index) => [
          fields[index].label,
          t(
            value === "effectiveYear"
              ? "csvImport.templateYearRequirement"
              : value === "commissioningYear"
                ? "csvImport.templateCommissioningYearRequirement"
                : fields[index].required
                  ? "csvImport.templateRequired"
                  : "csvImport.templateOptional",
          ),
          t(`csvImport.templateFieldPurpose.${value}`),
        ]),
        [],
        [t("csvImport.templateHelpNotice")],
        [t("csvImport.templateYearNotice")],
        [],
        [t("csvImport.templateTypeCode"), t("csvImport.templateTypeGermanLabel")],
        ...MUNICIPAL_PROPERTY_TYPE_LIST.map((type) => [type, t(`type.${type}`, { lng: "de" })]),
      ],
    });
  }
  return (
    <CsvImporter
      allowXlsx
      uploadTitle={t("csvImport.button")}
      strict
      rowLimit={MAX_PROPERTY_IMPORT_ROWS}
      maxFileSize={MAX_PROPERTY_IMPORT_BYTES}
      fields={fields}
      validateData={validateData}
      onImport={handleImport}
      isImportDisabled={result !== null}
      onClose={() => {
        setResult(null);
        setHasRefreshError(false);
      }}
      extraContent={
        <div className="flex flex-col gap-2 text-sm">
          <p>{t("csvImport.description")}</p>
          <p>{t("csvImport.templateYearNotice")}</p>
          <Button size="xs" variant="outline" disabled={progress !== null} onClick={handleDownloadTemplate}>
            {t("csvImport.template")}
          </Button>
          {progress !== null && <p role="status">{t("csvImport.progress", { count: progress })}</p>}
          {result && (
            <div role="status">
              <p>{t("csvImport.finished")}</p>
              <ul className="max-h-40 overflow-auto">
                {result.map((row, index) => (
                  <li key={index}>
                    {index + 2}: {row.name} — <span>{t(`csvImport.status.${row.status}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {hasRefreshError && <p role="alert">{t("csvImport.refreshError")}</p>}
        </div>
      }
    >
      <Button size="xs" variant="outline" className="gap-2 [&_svg]:size-3.5">
        <FileDown data-icon="inline-start" aria-hidden="true" />
        {t("csvImport.button")}
      </Button>
    </CsvImporter>
  );
}
